import { useQueryClient } from "@tanstack/react-query";
import { useState, useTransition } from "react";
import {
  ACTIVITY_DATE_DISPLAY_FIELDS,
  type ActivityDateFieldValue,
} from "@/components/clients/activity-date-fields";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { patch } from "@/lib/api/_client";
import { useT } from "@/lib/i18n/use-t";
import { ACTIVITY_DATE_LABELS, T } from "./client-display-settings-wrapper.i18n";

type ClientDisplaySettingsWrapperProps = {
  initialDisplayDates: string[];
};

export function ClientDisplaySettingsWrapper({
  initialDisplayDates,
}: ClientDisplaySettingsWrapperProps) {
  const t = useT(T);
  const [selectedFields, setSelectedFields] = useState<string[]>(initialDisplayDates);
  const [isPending, startTransition] = useTransition();
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleToggle = (fieldValue: string) => {
    setSelectedFields((current) => {
      if (current.includes(fieldValue)) {
        // Remove if already selected
        return current.filter((f) => f !== fieldValue);
      } else if (current.length < 3) {
        // Add if under limit
        return [...current, fieldValue];
      }
      // At limit, don't add
      return current;
    });
    setSuccess(false);
  };

  const queryClient = useQueryClient();

  const handleSave = () => {
    setError(null);
    setSuccess(false);

    startTransition(async () => {
      try {
        await patch("/internal/client-settings/display-dates", { fields: selectedFields });
        setSuccess(true);
        queryClient.invalidateQueries({ queryKey: ["displayDates"] });
      } catch (err: any) {
        setError(err.message || t.failedToSaveSettings);
      }
    });
  };

  const hasChanges =
    JSON.stringify(selectedFields.sort()) !== JSON.stringify([...initialDisplayDates].sort());

  const tDates = useT(ACTIVITY_DATE_LABELS);
  const getFieldLabel = (fieldValue: string): string =>
    tDates[fieldValue as ActivityDateFieldValue] ?? fieldValue;

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t.clientCardDisplay}</CardTitle>
        <CardDescription>{t.clientCardDescription}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {error && (
          <div className="text-sm text-destructive bg-destructive/10 p-3 rounded-md">{error}</div>
        )}
        {success && (
          <div className="text-sm rounded-md border border-success/40 bg-success/10 p-3">
            {t.settingsSaved}
          </div>
        )}

        <div className="space-y-4">
          <div className="text-sm text-muted-foreground">
            {t.selectedCount(String(selectedFields.length))}
          </div>

          <div className="grid gap-3">
            {ACTIVITY_DATE_DISPLAY_FIELDS.map((value) => {
              const isSelected = selectedFields.includes(value);
              const isDisabled = !isSelected && selectedFields.length >= 3;

              return (
                <div key={value} className="flex items-center space-x-3">
                  <Checkbox
                    id={value}
                    checked={isSelected}
                    onCheckedChange={() => handleToggle(value)}
                    disabled={isDisabled}
                  />
                  <Label
                    htmlFor={value}
                    className={`font-normal ${isDisabled ? "text-muted-foreground" : ""}`}
                  >
                    {getFieldLabel(value)}
                  </Label>
                </div>
              );
            })}
          </div>
        </div>

        <div className="flex justify-end">
          <Button onClick={handleSave} disabled={!hasChanges} loading={isPending}>
            {t.save}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
